import { AlertCircle, CheckCircle, Circle, Clock, FileCheck, Flag, Rocket, Settings, Shield, Users, Zap, CircleCheck } from "lucide-react";
import { Button, Card, CardBody, Col, Row, Spinner } from "react-bootstrap";
import React, { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import ManageEvaluatorModal from "../../apps/components/ManageEvaluatorModal";
import { getProjects, getStageGatesCounter, IProject } from "../../project";
import { ApiResponse, IDashboardFilterData, IStageGateData, StageGateCountData } from "../../core/_models";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { useAuth, usePermission } from "../../auth";
import PermissionGate from "../../apps/components/UI/PermissionGate";
import StageGate from "./StageGate";
import SectionHeader from "./SectionHeader";
import { KTIcon } from "../../../../_theme/helpers";
import SkeletonCard from "../../apps/components/skeleton/SkeletonCard";
import { debounce } from 'lodash';
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

// Lazy load the ProjectStageGateCard component
const LazyProjectStageGateCard = lazy(() => import("./ProjectStageGateCard"));

interface StageGateInfo {
  stage_id: string;
  sipdmgate: number;
  fipdmgate: number;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  gateCompletionDates?: Record<number, string>;
}

interface Props {
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  filters: IDashboardFilterData;
  refreshData: boolean;
  setRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
}

// Memoized ProjectCard component outside to prevent re-renders
const ProjectCard = React.memo(({
  project,
  stageGateInfo,
  getStatusIcon,
  handleManageEvaluator,
  handleProjectClick,
  lastSelectedProject
}: any) => {
  return (
    <Col sm={12} md={6} lg={4}>
      <Suspense fallback={<SkeletonCard wrapperClassName="" className="mb-4" titleWidth="100%" valueWidth="100%" showIcon={false} lines={10} />}>
        <LazyProjectStageGateCard
          project={project}
          stageGateInfo={stageGateInfo}
          getStatusIcon={getStatusIcon}
          handleManageEvaluator={handleManageEvaluator}
          handleProjectClick={handleProjectClick}
          lastSelectedProject={lastSelectedProject}
        />
      </Suspense>
    </Col>
  );
});

ProjectCard.displayName = 'ProjectCard';

const StageGates: React.FC<Props> = ({ setShowCreateModal, filters, refreshData, setRefreshData }) => {
  const { auth } = useAuth();
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();

  // State management
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [stageGateLoading, setStageGateLoading] = useState<boolean>(false);
  const [showManageEvaluator, setShowManageEvaluator] = useState<boolean>(false);
  const [activeProject, setActiveProject] = useState<any>(null);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [stageGateCountData, setStageGateCountData] = useState<StageGateCountData>({});
  const [lastSelectedProject, setLastSelectedProject] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(12); // Reduced to 12 for better performance
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Refs for performance optimization
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isFirstRender = useRef<boolean>(true);
  const projectsAbortRef = useRef<AbortController | null>(null);
  const stageGateAbortRef = useRef<AbortController | null>(null);

  // Memoized stage gate info - static data
  const stageGateInfo = useMemo<Record<number, StageGateInfo>>(() => ({
    1: {
      stage_id: 'project_stage_0',
      sipdmgate: 1,
      fipdmgate: 0,
      name: 'Project Initiation',
      description: 'Project charter, initial business case, and stakeholder identification',
      icon: Flag,
      color: 'blue'
    },
    2: {
      stage_id: 'project_stage_1',
      sipdmgate: 1,
      fipdmgate: 1,
      name: 'Infrastructure Planning',
      description: 'Detailed requirements gathering, analysis, and documentation',
      icon: FileCheck,
      color: 'purple'
    },
    3: {
      stage_id: 'project_stage_2',
      sipdmgate: 1,
      fipdmgate: 2,
      name: 'Strategic Resourcing',
      description: 'Technical architecture, system design, and solution planning',
      icon: Settings,
      color: 'indigo'
    },
    4: {
      stage_id: 'project_stage_3',
      sipdmgate: 1,
      fipdmgate: 3,
      name: 'Prefeasibility',
      description: 'System development, procurement processes, and vendor selection',
      icon: Zap,
      color: 'yellow'
    },
    5: {
      stage_id: 'project_stage_4',
      sipdmgate: 2,
      fipdmgate: 4,
      name: 'Feasibility',
      description: 'System integration, testing phases, and quality assurance',
      icon: Shield,
      color: 'orange'
    },
    6: {
      stage_id: 'project_stage_5',
      sipdmgate: 3,
      fipdmgate: 5,
      name: 'Design Development',
      description: 'Deployment strategy, rollout planning, and change management',
      icon: Users,
      color: 'teal'
    },
    7: {
      stage_id: 'project_stage_6',
      sipdmgate: 4,
      fipdmgate: 6,
      name: 'Design Documentation',
      description: 'System deployment, user training, and go-live activities',
      icon: Rocket,
      color: 'green'
    },
    8: {
      stage_id: 'project_stage_7',
      sipdmgate: 5,
      fipdmgate: 7,
      name: 'Works',
      description: 'Knowledge transfer, operations documentation, and support setup',
      icon: CheckCircle,
      color: 'emerald'
    },
    9: {
      stage_id: 'project_stage_8',
      sipdmgate: 6,
      fipdmgate: 8,
      name: 'Handover',
      description: 'Final deliverables, lessons learned, and project sign-off',
      icon: Circle,
      color: 'gray'
    },
    10: {
      stage_id: 'project_stage_9',
      sipdmgate: 7,
      fipdmgate: 9,
      name: 'Completion',
      description: 'Final deliverables, lessons learned, and project sign-off',
      icon: CircleCheck,
      color: 'gray'
    }
  }), []);

  // Memoized gate stats
  const gateStats = useMemo<IStageGateData[]>(() => {
    return Object.keys(stageGateInfo).map(gateNum => {
      const gate = parseInt(gateNum);
      const total = stageGateCountData[`gate_${gate - 1}`] || 0;
      const stageGate = stageGateInfo[gate as keyof typeof stageGateInfo];
      return {
        fipdmgate: stageGate.fipdmgate,
        sipdmgate: stageGate.sipdmgate,
        name: stageGate.name,
        total: total
      };
    });
  }, [stageGateInfo, stageGateCountData]);

  // Memoized getStatusIcon function
  const getStatusIcon = useCallback((status: string) => {
    switch (status) {
      case 'completed':
        return CheckCircle;
      case 'current':
        return Clock;
      case 'blocked':
        return AlertCircle;
      case 'upcoming':
        return Circle;
      default:
        return Circle;
    }
  }, []);

  // Debounced search handler
  const handleSearchChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setPage(1);
      setProjects([]);
    }, 500),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      handleSearchChange.cancel();
    };
  }, [handleSearchChange]);

  const hasMoreProjects = projects.length < totalRecords;
  const loadedProjectCount = projects.length;

  const handleManageEvaluator = useCallback((projectId: string) => {
    setActiveProject(projectId);
    setShowManageEvaluator(true);
  }, []);

  const handleProjectClick = useCallback((Id: string) => {
    sessionStorage.setItem("projectScroll", window.scrollY.toString());
    sessionStorage.setItem('lastSelectedProject', Id);
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      setRefreshData(false);

      projectsAbortRef.current?.abort();
      const controller = new AbortController();
      projectsAbortRef.current = controller;

      const projectResponse = await getProjects(
        filters,
        'none',
        'No',
        '',
        page,
        itemsPerPage,
        searchTerm,
        null,
        null,
        true,
        controller.signal
      ) as ApiResponse<any>;

      if (controller.signal.aborted) return;

      if (projectResponse.status === "ok") {
        const projectData = projectResponse.data?.projects || [];
        if (page === 1) {
          setProjects(projectData);
        } else {
          setProjects((prevProjects) => [...prevProjects, ...projectData]);
        }
        setTotalRecords(projectResponse.data?.pagination?.total_records || projectData.length || 0);
      } else {
        if (projectResponse && typeof projectResponse === 'object' && 'message' in projectResponse) {
          showSnackbar((projectResponse as any).message, "danger");
        }
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch projects:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), "danger");
    } finally {
      if (!projectsAbortRef.current?.signal.aborted) {
        setProjectsLoading(false);
      }
    }
  }, [filters, page, itemsPerPage, searchTerm, showSnackbar, setRefreshData]);

  const fetchStageGateCounts = useCallback(async () => {
    stageGateAbortRef.current?.abort();
    const controller = new AbortController();
    stageGateAbortRef.current = controller;

    try {
      setStageGateLoading(true);
      const stageGateWebPageResp = await getStageGatesCounter(filters, controller.signal);
      if (controller.signal.aborted) return;
      if (stageGateWebPageResp.status === "ok") {
        setStageGateCountData(stageGateWebPageResp.data);
      } else {
        showSnackbar(stageGateWebPageResp.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch stage gate counts:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch stage gate counts. Please try again."), "danger");
    } finally {
      if (!controller.signal.aborted) {
        setStageGateLoading(false);
      }
    }
  }, [filters, showSnackbar]);

  // Load project list when auth, filters, page, or searchTerm changes
  useEffect(() => {
    if (page === 1) {
      setProjects([]);
    }

    const timeoutId = setTimeout(() => {
      fetchProjects();
    }, isFirstRender.current ? 100 : 0);

    isFirstRender.current = false;

    return () => {
      clearTimeout(timeoutId);
      projectsAbortRef.current?.abort();
    };
  }, [filters, page, searchTerm, fetchProjects]);

  // Load stage gate counts when auth or filters change
  useEffect(() => {
    fetchStageGateCounts();
    return () => {
      stageGateAbortRef.current?.abort();
    };
  }, [filters, fetchStageGateCounts]);

  // Run on refreshData change
  useEffect(() => {
    if (refreshData) {
      setPage(1);
      setProjects([]);
      fetchStageGateCounts();
      fetchProjects().then(() => setRefreshData(false));
    }
  }, [refreshData, fetchStageGateCounts, fetchProjects, setRefreshData]);

  // Scroll restoration effect with performance improvement
  useEffect(() => {
    const savedScroll = sessionStorage.getItem("projectScroll");
    const lastSelectedProjectId = sessionStorage.getItem('lastSelectedProject');

    if (!savedScroll || projects.length === 0) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        window.scrollTo({
          top: parseInt(savedScroll),
          behavior: "auto"
        });
        setLastSelectedProject(lastSelectedProjectId);
        sessionStorage.removeItem("projectScroll");
        sessionStorage.removeItem('lastSelectedProject');
      }, 50);
    });
  }, [projects]);

  const renderLoadMoreButton = useCallback(() => {
    if (!hasMoreProjects) return null;

    return (
      <div className="d-flex flex-column align-items-center mt-4 gap-2">
        <div className="text-gray-700">
          Showing {loadedProjectCount} of {totalRecords} projects
        </div>
        <Button
          className="btn-primary"
          onClick={() => setPage((prev) => prev + 1)}
          disabled={projectsLoading}
        >
          {projectsLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Loading...
            </>
          ) : (
            'Load more projects'
          )}
        </Button>
      </div>
    );
  }, [hasMoreProjects, loadedProjectCount, totalRecords, projectsLoading]);

  // Memoized project cards with virtualization-like approach
  const projectCards = useMemo(() => {
    return projects.map((project: IProject) => (
      <ProjectCard
        key={project.project_id}
        project={project}
        stageGateInfo={stageGateInfo}
        getStatusIcon={getStatusIcon}
        handleManageEvaluator={handleManageEvaluator}
        handleProjectClick={handleProjectClick}
        lastSelectedProject={lastSelectedProject}
      />
    ));
  }, [projects, stageGateInfo, getStatusIcon, handleManageEvaluator, handleProjectClick, lastSelectedProject]);

  // Optimize render with useMemo for static sections
  const headerSection = useMemo(() => (
    <SectionHeader
      title="FIDPM Stage Gates"
      description="Framework for Infrastructure Delivery and Procurement Management gate 0-9 (ISO 9001 compliant)."
      icon={Flag}
      action={auth?.user_details?.is_approver !== 'Yes' ? (
        <PermissionGate allowed={can('create_project', 'write')}>
          <Button variant="light-primary" size="lg" disabled={projectsLoading || stageGateLoading} onClick={() => setShowCreateModal(true)}>
            <KTIcon iconName='plus-square' className='fs-3 pe-0' />  New Project
          </Button>
        </PermissionGate>
      ) : undefined}
    />
  ), [auth, can, setShowCreateModal, projectsLoading, stageGateLoading]);

  const stageGateSection = useMemo(() => (
    <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100">
      <div className="mb-5">
        <p className="h1 fw-bold">Stage Gate Progress Overview</p>
        <p className="h3 text-muted fw-normal">Number of projects at each Gate</p>
      </div>
      {stageGateLoading ? (
        <div className="stage-wrapper position-relative">
          <div className="row flex-nowrap flex-xl-wrap overflow-auto overflow-xl-visible g-5 pb-7 pb-lx-3">
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="col-6 col-sm-3 col-md-2 col-xl">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="100%"
                  valueWidth="100%"
                  showIcon={false}
                  lines={2}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <StageGate gateStats={gateStats} />
      )}
    </div>
  ), [stageGateLoading, gateStats]);

  const searchSection = useMemo(() => (
    <Card className="my-5">
      <CardBody className="d-flex flex-wrap align-items-center justify-content-between w-100 p-4 p-sm-6">
        <div className="d-flex align-items-center mb-2 mb-sm-0">
          <p className="apple-subtitle h4 h2-sm fw-bold me-3 mb-0">Project Stage Gate Details</p>
        </div>
        <input
          ref={searchInputRef}
          type="text"
          className="form-control form-control-md"
          style={{ maxWidth: '300px', width: '100%' }}
          placeholder="Search projects..."
          onChange={(e) => handleSearchChange(e.target.value)}
          aria-label="Search projects"
        />
      </CardBody>
    </Card>
  ), [handleSearchChange, projectsLoading]);

  // Loading skeletons memoized
  const skeletonCards = useMemo(() => (
    <div className="row flex-nowrap flex-xl-wrap overflow-auto overflow-xl-visible g-5 pb-7 pb-lx-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="col-6 col-sm-3 col-md-4">
          <SkeletonCard
            wrapperClassName=""
            className="mb-4"
            titleWidth="100%"
            valueWidth="100%"
            showIcon={false}
            lines={10}
          />
        </div>
      ))}
    </div>
  ), []);

  // Empty state memoized
  const emptyState = useMemo(() => (
    <div className="text-center py-5">
      {searchTerm && (
        <Button variant="secondary" onClick={() => {
          setSearchTerm('');
          setPage(1);
          if (searchInputRef.current) {
            searchInputRef.current.value = '';
          }
        }}>
          Clear search
        </Button>
      )}
    </div>
  ), [searchTerm]);

  return (
    <>
      <div className="position-relative">
        {/* {loading && <LoadingOverlay loading />} */}
        {headerSection}
        {stageGateSection}
        {searchSection}

        {projectsLoading && projects.length === 0 ? (
          skeletonCards
        ) : (
          <>
            <Row className="g-4 g-md-5">
              {projectCards}
            </Row>
            {renderLoadMoreButton()}
          </>
        )}

        {!projectsLoading && projects.length === 0 && emptyState}
      </div>

      {showManageEvaluator && (
        <ManageEvaluatorModal
          project_id={activeProject}
          onClose={() => setShowManageEvaluator(false)}
        />
      )}
    </>
  );
};

// Add display name for debugging
StageGates.displayName = 'StageGates';

export { StageGates };